import { z } from "zod";
import { DatabaseSequenceSchema, OpaqueCursorSchema, PageLimitSchema, DecimalStringSchema, Sha256HexSchema, UuidSchema, } from "./primitives.js";
export const TaskStatusSchema = z.enum(["inbox", "planned", "in_progress", "review", "done"]);
export const TaskPrioritySchema = z.enum(["urgent", "high", "medium", "low", "none"]);
export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}, "Expected a real calendar date");
export const ProjectCreateCommandSchema = z
    .object({
    commandId: UuidSchema,
    projectId: UuidSchema,
    workspaceId: UuidSchema,
    name: z.string().trim().min(1).max(200),
    timezone: z.string().min(1).max(100),
})
    .strict();
export const ProjectUpdateCommandSchema = z
    .object({
    commandId: UuidSchema,
    projectId: UuidSchema,
    revision: DatabaseSequenceSchema,
    name: z.string().trim().min(1).max(200).optional(),
    timezone: z.string().min(1).max(100).optional(),
    archived: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.name !== undefined || value.timezone !== undefined || value.archived !== undefined, {
    message: "At least one project change is required",
});
export const TaskCreateCommandSchema = z
    .object({
    commandId: UuidSchema,
    taskId: UuidSchema,
    projectId: UuidSchema,
    title: z.string().trim().min(1).max(500),
    descriptionMarkdown: z.string().max(1_048_576).default(""),
    status: TaskStatusSchema,
    priority: TaskPrioritySchema.default("none"),
    dueOn: DateOnlySchema.nullable().default(null),
    beforeTaskId: UuidSchema.nullable(),
    afterTaskId: UuidSchema.nullable(),
    boardRevision: DatabaseSequenceSchema,
})
    .strict()
    .refine((value) => value.beforeTaskId === null || value.afterTaskId === null || value.beforeTaskId !== value.afterTaskId, {
    message: "Neighbor IDs must differ",
});
export const RankCommandSchema = z
    .object({
    commandId: UuidSchema,
    taskId: UuidSchema,
    fromStatus: TaskStatusSchema,
    toStatus: TaskStatusSchema,
    beforeTaskId: UuidSchema.nullable(),
    afterTaskId: UuidSchema.nullable(),
    taskRevision: DatabaseSequenceSchema,
    boardRevision: DatabaseSequenceSchema,
})
    .strict()
    .refine((value) => value.beforeTaskId === null || value.afterTaskId === null || value.beforeTaskId !== value.afterTaskId, {
    message: "Neighbor IDs must differ",
});
export const RankResultSchema = z
    .object({
    commandId: UuidSchema,
    taskId: UuidSchema,
    status: TaskStatusSchema,
    rank: DecimalStringSchema,
    taskRevision: DatabaseSequenceSchema,
    boardRevision: DatabaseSequenceSchema,
    columnOrder: z
        .array(z
        .object({
        taskId: UuidSchema,
        rank: DecimalStringSchema,
        revision: DatabaseSequenceSchema,
    })
        .strict())
        .nullable(),
    resultDigest: Sha256HexSchema,
})
    .strict();
export const ProjectProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    ownerAccountId: UuidSchema,
    name: z.string().min(1).max(200),
    timezone: z.string().min(1).max(100),
    boardRevision: DatabaseSequenceSchema,
    revision: DatabaseSequenceSchema,
    archived: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const ProjectListSchema = z.object({ items: z.array(ProjectProjectionSchema) }).strict();
export const TaskProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    projectId: UuidSchema,
    title: z.string().min(1).max(500),
    descriptionMarkdown: z.string().max(1_048_576),
    status: TaskStatusSchema,
    priority: TaskPrioritySchema,
    dueOn: DateOnlySchema.nullable(),
    rank: DecimalStringSchema,
    revision: DatabaseSequenceSchema,
    archived: z.boolean(),
    assigneeIds: z.array(UuidSchema).max(100),
    labels: z.array(z.string().min(1).max(100)).max(100),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const TaskListSchema = z.object({ items: z.array(TaskProjectionSchema) }).strict();
export const TaskUpdateCommandSchema = z
    .object({
    commandId: UuidSchema,
    taskId: UuidSchema,
    revision: DatabaseSequenceSchema,
    boardRevision: DatabaseSequenceSchema.optional(),
    title: z.string().trim().min(1).max(500).optional(),
    descriptionMarkdown: z.string().max(1_048_576).optional(),
    priority: TaskPrioritySchema.optional(),
    dueOn: DateOnlySchema.nullable().optional(),
    assigneeIds: z.array(UuidSchema).max(100).refine((items) => new Set(items).size === items.length, "Duplicate assignees are not allowed").optional(),
    labels: z.array(z.string().trim().min(1).max(100)).max(100).refine((items) => new Set(items).size === items.length, "Duplicate labels are not allowed").optional(),
    archived: z.boolean().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const hasTaskChange = value.title !== undefined ||
        value.descriptionMarkdown !== undefined ||
        value.priority !== undefined ||
        value.dueOn !== undefined ||
        value.assigneeIds !== undefined ||
        value.labels !== undefined ||
        value.archived !== undefined;
    if (!hasTaskChange)
        context.addIssue({ code: "custom", message: "At least one task change is required" });
    if (value.archived !== undefined && value.boardRevision === undefined) {
        context.addIssue({ code: "custom", path: ["boardRevision"], message: "Board revision is required for archive changes" });
    }
});
export const TaskUpdateResultSchema = z
    .object({
    commandId: UuidSchema,
    taskId: UuidSchema,
    taskRevision: DatabaseSequenceSchema,
    boardRevision: DatabaseSequenceSchema,
    archived: z.boolean(),
})
    .strict();
export const TaskCommentCreateCommandSchema = z
    .object({
    commandId: UuidSchema,
    commentId: UuidSchema,
    taskId: UuidSchema,
    bodyMarkdown: z.string().trim().min(1).max(1_048_576),
})
    .strict();
export const TaskCommentProjectionSchema = z
    .object({
    id: UuidSchema,
    taskId: UuidSchema,
    accountId: UuidSchema,
    bodyMarkdown: z.string().max(1_048_576),
    revision: DatabaseSequenceSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const TaskCommentListSchema = z
    .object({
    items: z.array(TaskCommentProjectionSchema).max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const TaskActivityProjectionSchema = z
    .object({
    id: UuidSchema,
    taskId: UuidSchema,
    actorAccountId: UuidSchema,
    type: z.enum(["created", "updated", "ranked", "commented", "archived", "restored"]),
    taskRevision: DatabaseSequenceSchema,
    boardRevision: DatabaseSequenceSchema,
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
export const TaskActivityListSchema = z
    .object({
    items: z.array(TaskActivityProjectionSchema).max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const TaskListQuerySchema = z
    .object({
    workspaceId: UuidSchema.nullable().default(null),
    projectId: UuidSchema.nullable().default(null),
    assignee: z.literal("me").nullable().default(null),
    includeArchived: z.boolean().default(false),
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(100),
})
    .strict()
    .refine((value) => value.workspaceId !== null || value.projectId !== null || value.assignee === "me", "A workspace, project, or effective-account assignee scope is required");
export const TaskBoardItemSchema = TaskProjectionSchema.extend({
    commentCount: z.number().int().nonnegative(),
    activityCount: z.number().int().nonnegative(),
}).strict();
export const TaskBoardResultSchema = z
    .object({
    items: z.array(TaskBoardItemSchema).max(250),
    boardRevision: DatabaseSequenceSchema,
    columnOrder: z.array(TaskStatusSchema).length(5),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const TaskScopedListSchema = z
    .object({
    items: z.array(TaskBoardItemSchema).max(250),
    projectBoardRevisions: z
        .array(z
        .object({
        projectId: UuidSchema,
        boardRevision: DatabaseSequenceSchema,
    })
        .strict())
        .max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const TaskPageQuerySchema = z
    .object({
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(100),
})
    .strict();
//# sourceMappingURL=work.js.map